import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { requestPresignedDownload } from '../../../../api/files';
import { getPresignedUrl } from '../../../../lib/presigned-url-cache';
import { displayNameOf, localDayKey, type DatedAttachment } from '../../../../lib/attachment-groups';
import { ChevronLeft, ChevronRight, Download, X } from '../../../../icons';
import { AttachmentSaveMenu } from '../../../../components/Attachment/AttachmentSaveMenu';
import { tChat } from '../../../../lib/chat-i18n';
import { useEscapeClose } from '../../../../hooks/useEscapeClose';
import { useBodyScrollLock } from '../../../../hooks/useBodyScrollLock';
import { formatDayGroupLabel } from './MediaDayGroup';

/**
 * 이미지 라이트박스 (chat-media-date-groups — 2026-08-07).
 *
 * 기존 썸네일은 클릭하면 presigned URL 을 **새 탭**으로 열었다. 파일은 우측 미리보기 패널을 갖는데
 * 이미지만 앱 밖으로 튕겨 나가 대칭이 깨져 있었다("이미지도 파일과 동일한 기능" 요구).
 *
 * ←/→ 이동은 **필터·정렬이 적용된 전체 이미지 목록**을 대상으로 한다 — 접혀 있는 일자의 이미지까지
 * 순회하므로 아코디언이 탐색을 가두지 않는다.
 *
 * body 로 portal 한다(채팅 사이드바의 overflow/transform 밖으로 빼기 위함). z-index 는 mermaid
 * 라이트박스와 같은 계층 관용(`calc(var(--z-modal) - 1)`)을 따른다.
 */
interface MediaLightboxProps {
  /** chat-attachment-save — 대화 안에서 열렸으면 워크스페이스로 담을 수 있다. */
  roomId?: string;
  images: readonly DatedAttachment[];
  index: number;
  onIndexChange: (next: number) => void;
  onClose: () => void;
}

export function MediaLightbox({ images, index, onIndexChange, onClose, roomId }: MediaLightboxProps) {
  const total = images.length;
  const current = images[index];
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEscapeClose(onClose);
  useBodyScrollLock(true);

  const goPrev = useCallback(() => {
    if (total > 0) onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange]);
  const goNext = useCallback(() => {
    if (total > 0) onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    setUrl(null);
    setFailed(false);
    getPresignedUrl(current.fileId)
      .then((u) => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [current]);

  const handleDownload = useCallback(async () => {
    if (!current) return;
    try {
      const res = await requestPresignedDownload(current.fileId);
      const a = document.createElement('a');
      a.href = res.downloadUrl;
      a.download = res.name;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      /* 다운로드 실패 — 조용히 무시(사용자 재시도 가능) */
    }
  }, [current]);

  if (!current) return null;

  const name = displayNameOf(current);
  const dayLabel = formatDayGroupLabel(localDayKey(current.createdAt));

  return createPortal(
    <div
      className="chat-media-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={tChat('chat.media.lightbox_aria')}
      onClick={onClose}
    >
      <div className="chat-media-lightbox-toolbar" onClick={(e) => e.stopPropagation()}>
        <span className="chat-media-lightbox-caption">
          {name}
          <span className="chat-media-lightbox-day">{dayLabel}</span>
        </span>
        <span className="chat-media-lightbox-pos">
          {tChat('chat.media.lightbox_position', { index: index + 1, total })}
        </span>
        {/* chat-attachment-save — 큰 그림에서도 어디에 둘지 고른다. */}
        <AttachmentSaveMenu
          roomId={roomId}
          fileId={current.fileId}
          onDownload={handleDownload}
          buttonLabel={tChat('chat.media.download_aria', { name })}
        >
          <Download size={16} aria-hidden />
        </AttachmentSaveMenu>
        <button
          type="button"
          onClick={onClose}
          aria-label={tChat('chat.media.lightbox_close')}
          title={tChat('chat.media.lightbox_close')}
        >
          <X size={16} aria-hidden />
        </button>
      </div>

      <div className="chat-media-lightbox-stage" onClick={(e) => e.stopPropagation()}>
        {total > 1 && (
          <button
            type="button"
            className="chat-media-lightbox-nav chat-media-lightbox-nav--prev"
            onClick={goPrev}
            aria-label={tChat('chat.media.lightbox_prev')}
            title={tChat('chat.media.lightbox_prev')}
          >
            <ChevronLeft size={22} aria-hidden />
          </button>
        )}

        {failed ? (
          <p className="chat-media-lightbox-error">{tChat('chat.media.lightbox_failed')}</p>
        ) : url ? (
          <img className="chat-media-lightbox-img" src={url} alt={name} />
        ) : (
          <div className="chat-media-lightbox-loading" aria-busy />
        )}

        {total > 1 && (
          <button
            type="button"
            className="chat-media-lightbox-nav chat-media-lightbox-nav--next"
            onClick={goNext}
            aria-label={tChat('chat.media.lightbox_next')}
            title={tChat('chat.media.lightbox_next')}
          >
            <ChevronRight size={22} aria-hidden />
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
